import { Menu, PaginatedSearchResultsMenu } from "client/jspPlatformExperiment";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import jspPlatformApi from "client/portals";
import {
  isApiError,
  isErrorResponseBodyType,
  toJspApiError,
} from "models/utils/apiUtils";
import { isMenuArray } from "models/menu/menu";
import { menuBatchOperationActions } from "./menuBatchOperationSlice";

interface MenuOptionsState {
  data: Record<number, Menu>;
  isLoading: boolean;
  error: string | null | undefined;
}

const initialState: MenuOptionsState = {
  data: [],
  isLoading: false,
  error: null,
};

const fetchAllMenuOptions = createAsyncThunk(
  "menuOptions/fetchAllMenuOptions",
  async (_: null | undefined, { rejectWithValue }) =>
    jspPlatformApi()
      .menu.getMenuMealSearch()
      .then((res) => {
        const raw = res.data as PaginatedSearchResultsMenu | undefined;
        const data = raw?.data;
        if (data != null && isMenuArray(data)) {
          return data;
        }
        return [];
      })
      .catch((error) => {
        let errMsg: string;
        const err = isApiError(error) ? toJspApiError(error) : null;
        if (err && isErrorResponseBodyType(err.body)) {
          errMsg = err.body.detail;
        } else {
          errMsg = "Unknown error!";
        }
        return rejectWithValue(errMsg);
      }),
);

const setMenuOptions = (
  state: MenuOptionsState,
  action: PayloadAction<Menu[] | null>,
): MenuOptionsState => {
  return {
    ...state,
    data: {
      ...state.data,
      ...action.payload?.reduce<Record<number, Menu>>((acc, newMenu) => {
        acc[newMenu.id] = newMenu;
        return acc;
      }, {}),
    },
  };
};

export const menuOptionsSlice = createSlice({
  name: "menuOptionsSlice",
  initialState,
  reducers: {
    setMenuOptions,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAllMenuOptions.pending, (state) => {
      return { ...state, isLoading: true };
    });
    builder.addCase(fetchAllMenuOptions.fulfilled, (state, action) => {
      return {
        ...state,
        ...setMenuOptions(state, action),
        isLoading: false,
      };
    });
    builder.addCase(fetchAllMenuOptions.rejected, (state, action) => {
      return {
        ...state,
        isLoading: false,
        error: action.payload as string,
      };
    });
    builder.addCase(
      menuBatchOperationActions.batchUpdateMenus.fulfilled,
      (state, action) => {
        return {
          ...state,
          data: {
            ...state.data,
            ...action.payload.reduce<Record<number, Menu>>(
              (acc, updatedMenu) => {
                acc[updatedMenu.id] = updatedMenu;
                return acc;
              },
              {},
            ),
          },
        };
      },
    );
  },
});

export const menuOptionsActions = {
  ...menuOptionsSlice.actions,
  fetchAllMenuOptions,
};

export const menuOptionsReducer = menuOptionsSlice.reducer;

export default menuOptionsReducer;
